/**
 * stillwater — a calming space for people moving through a manic episode.
 * by David Hunt
 */
import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { Anchor, Wind, Info, Moon, Film } from "lucide-react";
import BackgroundGradient, {
  DEFAULT_PRESET_ID,
  getPresetById,
  getCouplingForPreset,
} from "@/components/calm/BackgroundGradient";
import LightPicker from "@/components/calm/LightPicker";
import QuoteDisplay from "@/components/calm/QuoteDisplay";
import AudioControls from "@/components/calm/AudioControls";
import GroundingFlow from "@/components/calm/GroundingFlow";
import BreathingExercise from "@/components/calm/BreathingExercise";
import AboutOverlay from "@/components/calm/AboutOverlay";
import SleepSpace from "@/components/calm/SleepSpace";
import DriftView from "@/components/calm/DriftView";
import TrustedContact from "@/components/calm/TrustedContact";
import MoodWave from "@/components/calm/MoodWave";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  isBedtimeWindow,
  hasShownBedtimeToastTonight,
  markBedtimeToastShown,
} from "@/lib/bedtime";
import { useIdle } from "@/lib/useIdle";

const PRESET_STORAGE_KEY = "stillwater.lightPreset";
// Self-contained modal overlays (scene *weather* overlays like mist-overlay
// must NOT match — they live on the background, not over it).
const MODAL_OVERLAY_SELECTOR =
  '[data-testid="about-overlay"],[data-testid="favorites-overlay"],[data-testid="trusted-contact-overlay"],[data-testid="mood-wave-overlay"]';

function App() {
  const [groundingOpen, setGroundingOpen] = useState(false);
  const [breathingOpen, setBreathingOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [sleepOpen, setSleepOpen] = useState(false);
  const [driftOpen, setDriftOpen] = useState(false);
  const [presetId, setPresetId] = useState(DEFAULT_PRESET_ID);
  const audioControlsRef = useRef(null);
  const overlayOpenRef = useRef(false);
  // After 7s of stillness, controls sink into the background — the header
  // stays faintly visible (33%) so the paths are still there to choose.
  const idle = useIdle(7000);
  // Arrival memory — read before AudioControls mounts (its effects would
  // otherwise overwrite last session's value).
  const [arrival] = useState(() => {
    try {
      return {
        wasPlaying: localStorage.getItem("stillwater.wasPlaying") === "true",
        name: localStorage.getItem("stillwater.lastPlaceName") || "",
      };
    } catch (_) {
      return { wasPlaying: false, name: "" };
    }
  });
  const arrivalShownRef = useRef(false);

  // If sound was playing when they left, their place is waiting — offer a
  // one-tap return (browsers require a gesture to resume audio).
  useEffect(() => {
    if (!arrival.wasPlaying) return;
    const t = setTimeout(() => {
      if (document.querySelector(MODAL_OVERLAY_SELECTOR)) return;
      arrivalShownRef.current = true;
      toast(
        arrival.name
          ? `${arrival.name} is still here`
          : "your calm place is waiting",
        {
          description: "pick up where you left off?",
          action: {
            label: "return",
            onClick: () => {
              if (audioControlsRef.current?.resumeLastPlace) {
                audioControlsRef.current.resumeLastPlace();
              }
            },
          },
          duration: 12000,
        },
      );
    }, 2000);
    return () => clearTimeout(t);
  }, [arrival]);

  useEffect(() => {
    overlayOpenRef.current =
      driftOpen || sleepOpen || breathingOpen || groundingOpen || aboutOpen;
    // Dismiss any lingering toast when a full-screen overlay takes over.
    if (overlayOpenRef.current) toast.dismiss();
  }, [driftOpen, sleepOpen, breathingOpen, groundingOpen, aboutOpen]);

  const handleSleepAudioAutoStart = (soundId) => {
    if (audioControlsRef.current) {
      audioControlsRef.current.autoStartCoupled(soundId);
    }
  };

  const handleSleepAudioFadeOut = (seconds) => {
    if (audioControlsRef.current && audioControlsRef.current.fadeOutOverSeconds) {
      audioControlsRef.current.fadeOutOverSeconds(seconds);
    }
  };

  const handleBreathModulate = (phase, seconds) => {
    if (audioControlsRef.current && audioControlsRef.current.applyBreathModulation) {
      audioControlsRef.current.applyBreathModulation(phase, seconds);
    }
  };

  const handleBreathModulateEnd = () => {
    if (audioControlsRef.current && audioControlsRef.current.endBreathModulation) {
      audioControlsRef.current.endBreathModulation();
    }
  };

  // Load saved preset once on mount.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(PRESET_STORAGE_KEY);
      if (saved && getPresetById(saved).id === saved) {
        setPresetId(saved);
      }
    } catch (_) {
      /* localStorage may be unavailable — silent */
    }
  }, []);

  // Bedtime nudge — after 22:00 local time, on the first landing of the
  // night, offer a soft one-tap into Sleep Space. Fires at most once per
  // night (see lib/bedtime.js) and only if the user hasn't already opened
  // Sleep Space via some other path this session.
  useEffect(() => {
    if (!isBedtimeWindow()) return;
    if (hasShownBedtimeToastTonight()) return;
    // Delay a beat so the toast doesn't feel like a modal jump-cut. Users
    // get time to see the app first; the nudge arrives as if the app is
    // gently noticing the hour.
    const t = setTimeout(() => {
      // If the user already opened a full-screen overlay (drift, sleep,
      // mood, trusted contact, favorites...) don't fire — the nudge would
      // land on top of it.
      if (overlayOpenRef.current) return;
      if (document.querySelector(MODAL_OVERLAY_SELECTOR)) return;
      // The arrival nudge already spoke this visit — one voice at a time.
      if (arrivalShownRef.current) return;
      markBedtimeToastShown();
      toast("it's late — try sleep space?", {
        description:
          "a long breath or a body scan can help a racing mind wind down.",
        action: {
          label: "take me there",
          onClick: () => setSleepOpen(true),
        },
        duration: 12000,
      });
    }, 3500);
    return () => clearTimeout(t);
  }, []);

  const handlePresetChange = (id) => {
    setPresetId(id);
    try {
      localStorage.setItem(PRESET_STORAGE_KEY, id);
    } catch (_) {
      /* silent */
    }
    // If the newly-picked scene has a coupled ambient sound, offer it softly.
    // The AudioControls component decides whether to honor this (it won't if
    // the user has already taken manual control of the audio).
    const coupling = getCouplingForPreset(id);
    if (coupling && audioControlsRef.current) {
      audioControlsRef.current.autoStartCoupled(coupling.soundId);
    }
  };

  // A blend carries its own place — wash the screen in the matching scene
  // without triggering the scene→sound auto-coupling (the blend IS the sound).
  const handleBlendScene = (sceneId) => {
    setPresetId(sceneId);
    try {
      localStorage.setItem(PRESET_STORAGE_KEY, sceneId);
    } catch (_) {
      /* silent */
    }
  };

  return (
    <div className="App" data-testid="calm-app">
      <BackgroundGradient presetId={presetId} />

      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: idle ? 0.33 : 1, y: 0 }}
        transition={{ duration: 2.0, ease: "easeInOut", delay: idle ? 0 : 0.4 }}
        className="relative z-10 flex items-center justify-between gap-3 px-4 sm:px-12 pt-6 sm:pt-8 safe-top safe-left safe-right"
      >
        <div className="flex flex-col leading-tight" data-testid="app-title">
          <span className="font-heading italic text-white/85 text-lg sm:text-xl">
            stillwater
          </span>
          <span className="text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-white/40">
            a place to slow down
          </span>
        </div>

        <div className="flex items-center gap-1 sm:gap-3">
          <button
            onClick={() => setBreathingOpen(true)}
            className="inline-flex items-center gap-2 rounded-full w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-2.5 justify-center backdrop-blur-xl bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 text-sm transition-colors duration-500"
            data-testid="open-breathing-btn"
            aria-label="Open the 4-7-8 breathing exercise"
          >
            <Wind size={15} strokeWidth={1.5} />
            <span className="hidden sm:inline">breathe</span>
          </button>
          <button
            onClick={() => setGroundingOpen(true)}
            className="inline-flex items-center gap-2 rounded-full w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-2.5 justify-center backdrop-blur-xl bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 text-sm transition-colors duration-500"
            data-testid="open-grounding-btn"
            aria-label="Open the 5-4-3-2-1 grounding exercise"
          >
            <Anchor size={15} strokeWidth={1.5} />
            <span className="hidden sm:inline">ground me</span>
          </button>
          <button
            onClick={() => setSleepOpen(true)}
            className="inline-flex items-center gap-2 rounded-full w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-2.5 justify-center backdrop-blur-xl bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 text-sm transition-colors duration-500"
            data-testid="open-sleep-btn"
            aria-label="Open the sleep space — for a racing mind at bedtime"
          >
            <Moon size={15} strokeWidth={1.5} />
            <span className="hidden sm:inline">sleep</span>
          </button>
          <button
            onClick={() => setDriftOpen(true)}
            className="inline-flex items-center gap-2 rounded-full w-10 h-10 sm:w-auto sm:h-auto sm:px-5 sm:py-2.5 justify-center backdrop-blur-xl bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 text-sm transition-colors duration-500"
            data-testid="open-drift-btn"
            aria-label="Open a full-screen calming visual"
          >
            <Film size={15} strokeWidth={1.5} />
            <span className="hidden sm:inline">drift</span>
          </button>
          <button
            onClick={() => setAboutOpen(true)}
            className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:text-white transition-colors duration-500"
            data-testid="open-about-btn"
            aria-label="About stillwater and privacy"
            title="about stillwater"
          >
            <Info size={15} strokeWidth={1.5} />
          </button>
        </div>
      </motion.header>

      {/* Vertical scene rail on the left — mobile-first. Sinks away when idle. */}
      <div
        className={`transition-opacity duration-[1500ms] ease-in-out ${
          idle ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        data-testid="light-picker-fader"
        data-idle={idle}
      >
        <LightPicker current={presetId} onChange={handlePresetChange} />
      </div>

      <main className="relative z-10 flex items-center justify-center min-h-[calc(100dvh-12rem)] px-6 sm:px-8 py-4">
        <QuoteDisplay />
      </main>

      <footer
        className={`fixed bottom-4 sm:bottom-6 left-0 right-0 z-10 flex justify-center px-3 sm:px-6 safe-bottom safe-left safe-right transition-opacity duration-[1500ms] ease-in-out ${
          idle ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        data-testid="audio-footer"
        data-idle={idle}
      >
        <AudioControls ref={audioControlsRef} onBlendScene={handleBlendScene} />
      </footer>

      <GroundingFlow
        open={groundingOpen}
        onClose={() => setGroundingOpen(false)}
      />

      <BreathingExercise
        open={breathingOpen}
        onClose={() => setBreathingOpen(false)}
        onBreathPhase={handleBreathModulate}
        onBreathEnd={handleBreathModulateEnd}
      />

      <AboutOverlay
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
      />

      <SleepSpace
        open={sleepOpen}
        onClose={() => setSleepOpen(false)}
        onAudioAutoStart={handleSleepAudioAutoStart}
        onAudioFadeOut={handleSleepAudioFadeOut}
        onBreathPhase={handleBreathModulate}
        onBreathEnd={handleBreathModulateEnd}
      />

      <DriftView open={driftOpen} onClose={() => setDriftOpen(false)} />

      <TrustedContact dimmed={idle} />

      <MoodWave dimmed={idle} />

      <Toaster
        position="top-center"
        offset={{ top: "calc(env(safe-area-inset-top, 0px) + 84px)" }}
        mobileOffset={{ top: "calc(env(safe-area-inset-top, 0px) + 72px)" }}
      />
    </div>
  );
}

export default App;
